export type BatchQueueOptions<T> = {
  maxSize: number;
  flushIntervalMs: number;
  onFlush: (events: T[], requeue: (events: T[]) => void) => void | Promise<void>;
};

export class BatchQueue<T> {
  private readonly options: BatchQueueOptions<T>;
  private queue: T[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: BatchQueueOptions<T>) {
    this.options = options;
  }

  enqueue(event: T): void {
    this.queue.push(event);
    if (this.queue.length >= this.options.maxSize) {
      void this.flush();
      return;
    }
    this.scheduleFlush();
  }

  async flush(): Promise<void> {
    this.clearTimer();
    if (this.queue.length === 0) return;

    const events = this.queue;
    this.queue = [];

    await this.options.onFlush(events, (failedEvents) => {
      this.queue = [...failedEvents, ...this.queue];
      this.scheduleFlush();
    });
  }

  private scheduleFlush(): void {
    if (this.timer !== undefined) return;
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.options.flushIntervalMs);
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
